import {useJourneyMetaConfig} from '@/hooks/useJourneyMetaConfig'
import {FieldLabel} from '@/shared-components/FieldLabel'
import {InfoOrError} from '@/shared-components/InfoOrError.tsx'
import {useAppSelector} from '@/store'
import {selectDarkMode} from '@/store/slices/themeSlice'
import {getAssignedSegmentsUseCase} from '@/useCases/getAssignedSegmentsUseCase'
import {useQueryClient} from '@tanstack/react-query'
import React, {useCallback, useEffect} from 'react'
import {useIntl} from 'react-intl'
import type {StylesConfig} from 'react-select'
import AsyncSelect from 'react-select/async'

interface Props {
  id?: string
  label: string
  onEnumServerDidChange: (value: string) => void
  errorMessage?: string
  selectedValue: string | null
  required?: boolean
  disabled?: boolean
}

type segmentOption = {
  value: string
  label: string
  id: string
}

export default function AssignedSegments({
  id,
  label,
  onEnumServerDidChange,
  errorMessage,
  selectedValue,
  required = false,
  disabled = false,
}: Props): React.JSX.Element {
  const {formatMessage} = useIntl()
  const {country} = useJourneyMetaConfig()
  const queryClient = useQueryClient()
  const isDark = useAppSelector(selectDarkMode)
  const focusColor = isDark ? '#669dd7' : '#005cbc'

  useEffect(() => {
    onEnumServerDidChange(selectedValue ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const customizeStyle: StylesConfig<segmentOption, false> = {
    control: (base, state) => {
      let borderColor = base.borderColor

      if (state.isFocused) {
        borderColor = focusColor
      } else if (isDark) {
        borderColor = '#616161'
      }

      return {
        ...base,
        minHeight: '32px',
        height: '32px',
        padding: 0,
        backgroundColor: isDark ? '#252525' : base.backgroundColor,
        borderColor,
        boxShadow: state.isFocused ? `0 0 0 1px ${focusColor}` : base.boxShadow,
        '&:hover': {borderColor: isDark ? '#818181' : '#616161'},
      }
    },
    valueContainer: base => ({
      ...base,
      padding: '0 8px',
    }),
    input: base => ({
      ...base,
      fontSize: '14px',
      color: isDark ? '#efefef' : '#706E6B',
    }),
    indicatorsContainer: base => ({
      ...base,
      height: '32px',
      color: isDark ? '#efefef' : base.color,
    }),
    dropdownIndicator: base => ({
      ...base,
      color: isDark ? '#efefef' : base.color,
      '&:hover': {color: isDark ? '#ffffff' : base.color},
    }),
    placeholder: base => ({
      ...base,
      fontSize: '14px',
      color: isDark ? '#9a9a9a' : '#706E6B',
    }),
    singleValue: base => ({
      ...base,
      fontSize: '14px',
      color: isDark ? '#efefef' : '#706E6B',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    menu: base => ({
      ...base,
      backgroundColor: isDark ? '#252525' : base.backgroundColor,
      border: isDark ? '1px solid #444444' : base.border,
    }),
    menuList: base => ({
      ...base,
      backgroundColor: isDark ? '#252525' : base.backgroundColor,
    }),
    option: (base, state) => {
      let backgroundColor = base.backgroundColor
      let color = base.color

      if (state.isSelected) {
        backgroundColor = isDark ? '#337dc9' : '#005cbc'
        color = '#ffffff'
      } else {
        if (state.isFocused) {
          backgroundColor = isDark ? '#333333' : '#deebff'
        } else if (isDark) {
          backgroundColor = '#252525'
        }

        if (isDark) {
          color = '#efefef'
        }
      }

      return {
        ...base,
        fontSize: '14px',
        padding: '6px 8px',
        minHeight: '24px',
        lineHeight: '20px',
        backgroundColor,
        color,
        '&:active': {backgroundColor: isDark ? '#444444' : '#b2d4ff'},
      }
    },
  }

  const loadOptions = useCallback(
    async (inputValue: string): Promise<segmentOption[]> => {
      const searchValue = inputValue === '' ? undefined : inputValue
      try {
        const res = await queryClient.fetchQuery({
          queryKey: ['assignedSegments', country, inputValue],
          queryFn: ({signal}) => getAssignedSegmentsUseCase({country, query: searchValue}, signal),
          staleTime: 5 * 60 * 1000,
        })

        return (res?.segments ?? []).map((segment: {name: string}) => ({
          value: segment.name,
          label: segment.name,
          id: segment.name,
        }))
      } catch (err) {
        console.error('Error fetching options:', err)
        return []
      }
    },
    [queryClient, country]
  )

  return (
    <div className="flex w-full min-w-fit flex-col gap-2">
      <FieldLabel id={id} label={label} required={required} disabled={disabled} />
      <div className="w-full min-w-fit">
        <AsyncSelect
          id="assigned-segments-select"
          styles={customizeStyle}
          defaultOptions={true}
          value={selectedValue ? {value: selectedValue, label: selectedValue, id: selectedValue} : null}
          loadOptions={loadOptions}
          onChange={option => {
            if (option) onEnumServerDidChange((option as {value: string}).value)
          }}
          noOptionsMessage={() => formatMessage({id: 'ENTRY_CONDITION_CONTENT.NO_SEGMENTS_FOUND'})}
          placeholder={formatMessage({id: 'ENTRY_CONDITION_CONTENT.SELECT_SEGMENTS'})}
          isDisabled={disabled}
        />
      </div>

      <InfoOrError error={errorMessage} />
    </div>
  )
}
